import { usePrefs } from '../app/prefs';
import { i18n } from '../i18n';

/** Locale-aware formatting (Intl) for the active UI language. */
const UNITS: Record<string, { unit: string; digits: number }> = {
  'electrical.voltage_v': { unit: 'V', digits: 1 },
  'electrical.current_a': { unit: 'A', digits: 3 },
  'electrical.power_w': { unit: 'W', digits: 2 },
  'electrical.power_factor': { unit: '', digits: 3 },
  'electrical.energy_wh': { unit: 'Wh', digits: 0 },
  'device.temperature_c': { unit: '°C', digits: 1 },
  'light.dimmer_pct': { unit: '%', digits: 0 },
  'electrical.apparent_power_va': { unit: 'VA', digits: 2 },
  'electrical.reactive_power_var': { unit: 'var', digits: 2 },
  'electrical.frequency_hz': { unit: 'Hz', digits: 2 },
  'electrical.leakage_current_ma': { unit: 'mA', digits: 1 },
  'environment.lux': { unit: 'lx', digits: 0 },
  // catalog metrics 16–56 (database/seeds/0004_protocol_completion_metrics.sql)
  'environment.illuminance_lux': { unit: 'lx', digits: 0 },
  'electrical.leakage_voltage_v': { unit: 'V', digits: 2 },
  'device.tilt_deg': { unit: '°', digits: 0 },
  'light.switch_count': { unit: '', digits: 0 },
  'light.switch_on_count': { unit: '', digits: 0 },
  'light.switch_off_count': { unit: '', digits: 0 },
  'light.photocell_segment': { unit: '', digits: 0 },
  'network.signal_pct': { unit: '%', digits: 0 },
  'network.rsrp_dbm': { unit: 'dBm', digits: 0 },
  'network.sinr_db': { unit: 'dB', digits: 0 },
  'network.rssi_dbm': { unit: 'dBm', digits: 0 },
  'environment.temperature_c': { unit: '°C', digits: 1 },
  'environment.humidity_pct': { unit: '%', digits: 1 },
  'environment.pressure_hpa': { unit: 'hPa', digits: 0 },
  'environment.pm1_ugm3': { unit: 'µg/m³', digits: 0 },
  'environment.pm25_ugm3': { unit: 'µg/m³', digits: 0 },
  'environment.pm10_ugm3': { unit: 'µg/m³', digits: 0 },
  'environment.wind_speed_ms': { unit: 'm/s', digits: 1 },
  'environment.rainfall_mmh': { unit: 'mm/h', digits: 1 },
  'environment.noise_db': { unit: 'dB', digits: 1 },
  'environment.radiation_wm2': { unit: 'W/m²', digits: 0 },
  'environment.uv_index': { unit: '', digits: 0 },
  'meter.voltage_a_v': { unit: 'V', digits: 1 },
  'meter.voltage_b_v': { unit: 'V', digits: 1 },
  'meter.voltage_c_v': { unit: 'V', digits: 1 },
  'meter.current_a_a': { unit: 'A', digits: 2 },
  'meter.current_b_a': { unit: 'A', digits: 2 },
  'meter.current_c_a': { unit: 'A', digits: 2 },
  'meter.frequency_hz': { unit: 'Hz', digits: 2 },
  'meter.active_power_kw': { unit: 'kW', digits: 2 },
  'meter.reactive_power_kvar': { unit: 'kvar', digits: 2 },
  'meter.apparent_power_kva': { unit: 'kVA', digits: 2 },
  'meter.power_factor': { unit: '', digits: 3 },
  'meter.energy_kwh': { unit: 'kWh', digits: 1 },
  'meter.reactive_energy_kvarh': { unit: 'kvarh', digits: 1 },
  'meter.leakage_current_ma': { unit: 'mA', digits: 1 },
};

/** 0/1 state metrics, shown as a word instead of a number. */
const FLAGS: Record<string, [string, string]> = {
  'device.tilt_warning': ['catalog.metricValue.tiltOk', 'catalog.metricValue.tiltAlarm'],
  'light.relay_closed': ['catalog.metricValue.relayOpen', 'catalog.metricValue.relayClosed'],
};
/** Durations reported in seconds, shown in hours. */
const HOURS = new Set([
  'light.on_time_s',
  'light.fault_time_s',
  'device.uptime_s',
  'network.connected_time_s',
]);

export function formatNumber(v: number | undefined, digits = 0): string {
  if (v === undefined || Number.isNaN(v)) return '—';
  return new Intl.NumberFormat(i18n.language, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(v);
}

export function formatMetric(key: string, v: number | undefined): string {
  if (HOURS.has(key) && v !== undefined) return `${formatNumber(v / 3600, 1)} h`;
  const flag = FLAGS[key];
  if (flag && (v === 0 || v === 1)) return i18n.t(flag[v] as 'catalog.metricValue.tiltOk');
  // energy counters are stored in Wh and shown in kWh, like the legacy platform
  if (key === 'electrical.energy_wh' && v !== undefined) return `${formatNumber(v / 1000, 3)} kWh`;
  const u = UNITS[key] ?? { unit: '', digits: 2 };
  const n = formatNumber(v, u.digits);
  return v === undefined || !u.unit ? n : `${n} ${u.unit}`;
}

export function formatRelative(iso: string | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const sec = Math.round((new Date(iso).getTime() - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto', style: 'short' });
  const abs = Math.abs(sec);
  if (abs < 60) return rtf.format(sec, 'second');
  if (abs < 3600) return rtf.format(Math.round(sec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(sec / 3600), 'hour');
  return rtf.format(Math.round(sec / 86400), 'day');
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * Date + time in the local time zone, laid out per the user's `dateFormat` preference (app/prefs.ts, read without
 * React because this runs outside components too): 'auto' = Intl short date + medium time for the UI language;
 * 'dd-mm-yyyy' / 'yyyy-mm-dd' = fixed numeric layouts with a 24 h clock and seconds.
 */
export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const layout = usePrefs.getState().dateFormat;
  if (layout === 'auto')
    return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'medium' }).format(d);
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  const [y, m, day] = [d.getFullYear(), pad2(d.getMonth() + 1), pad2(d.getDate())];
  return layout === 'dd-mm-yyyy' ? `${day}-${m}-${y} ${time}` : `${y}-${m}-${day} ${time}`;
}

/** Byte count with a binary unit (B, KB, MB, GB = ×1024, as mobile operators bill). */
export function formatBytes(v: number | undefined): string {
  if (v === undefined || Number.isNaN(v)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let x = v;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i++;
  }
  return `${formatNumber(x, i === 0 ? 0 : x < 10 ? 2 : 1)} ${units[i]}`;
}
